import { captureException } from '@sentry/react';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import { gql, useClient } from '@web/api/src';
import {
  ModalWrapper,
  PortalModal,
  getArticlePriceData,
  getInventoryArticleType,
  nextQuantity,
  useParameters,
} from '@web/utils/src';
import cartIconDark from '@web/utils/src/assets/icons/functions/cart-icon-dark.svg';
import plusIcon from '@web/utils/src/assets/icons/functions/plus-icon.svg';
import { useCustomToast } from '@/hooks/useCustomToast';
import {
  isCartOpenAtom,
  isReroderWarningEnabled,
  newCartItemAtom,
} from '@/store/atoms';
import { addToCartAtom } from '@/store/store';
import { Article } from '@/types';
import { AddToCartToast } from '../AddToCartToast';

export interface AddToCartButtonProps {
  article: Article;
  quantityInCart: number;
}

interface CartParams {
  retailerId: string;
}

const checkTotalQuery = gql`
  query getTotalLineItemsbyInventory($inventoryId: ID!) {
    inventory(id: $inventoryId) {
      orderLineItemsConnection(after: "0") {
        pageInfo {
          total
        }
      }
    }
  }
`;

const AddToCartButton = (props: AddToCartButtonProps) => {
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const isWarningEnabled = useAtomValue(isReroderWarningEnabled);
  const setCartItems = useSetAtom(addToCartAtom);
  const newCartItem = useSetAtom(newCartItemAtom);
  const setCartOpen = useSetAtom(isCartOpenAtom);
  const { retailerId } = useParameters<CartParams>();
  const { t } = useTranslation();
  const client = useClient();

  const { article, quantityInCart } = props;
  const itemType = getInventoryArticleType(article);
  const { finalPrice } = getArticlePriceData(article);
  const customToast = useCustomToast();

  const handleAddToCart = () => {
    let quantity = 1;

    if (
      itemType === 'loose' &&
      article.amount &&
      article.quantityOptions?.delivery
    ) {
      const deliveryOptions = article.quantityOptions?.delivery;
      if (!deliveryOptions || !deliveryOptions.length) {
        captureException(
          new Error(`quantity options not defined on article #${article.id}`),
        );
      }
      const incrementBy = nextQuantity(
        deliveryOptions as string[],
        quantityInCart,
      );
      quantity = incrementBy as number;
    }

    // previously this was handled conditionally and if price was falsy chat was initiated
    try {
      newCartItem(article);
      setCartItems({
        quantity,
        price: finalPrice,
        retailerID: retailerId,
      });
      toast(
        <AddToCartToast article={article} onClick={() => setCartOpen(true)} />,
        { duration: 3000, position: 'bottom-right' },
      );
    } catch (error: any) {
      customToast('error', error.message || t('global.something_went_wrong'));
    }
  };

  const handleClick = async () => {
    if (isWarningEnabled) {
      try {
        const result = await client.query(checkTotalQuery, {
          inventoryId: article.inventory[0].id,
        });
        const totalLineItems =
          result?.data?.inventory?.orderLineItemsConnection?.pageInfo?.total ??
          0;
        if (totalLineItems) {
          handleAddToCart();
        } else {
          setWarningModalOpen(true);
        }
      } catch {
        customToast('error', t('global.something_went_wrong'));
      }
    } else {
      handleAddToCart();
    }
  };

  const reorderWarningText = t('inventory.reorder_warning', {
    articleName: article.name,
  });
  const [beforeArticle, afterArticle] = reorderWarningText.split(article.name);

  return (
    <>
      {warningModalOpen && (
        <PortalModal
          isOpen={warningModalOpen}
          setIsOpen={setWarningModalOpen}
          data-testID="warning-modal"
        >
          <ModalWrapper
            setIsOpen={setWarningModalOpen}
            submitText={t('inventory.warning_accept')}
            cancelText={t('inventory.warning_decline')}
            t={t}
            submitHandler={handleAddToCart}
          >
            <p className="py-2-0">
              {beforeArticle}
              <strong>{article.name}</strong>
              {afterArticle}
            </p>
          </ModalWrapper>
        </PortalModal>
      )}
      <div
        className="bg-primary-yellow rounded-base p-2-0 md:p-0-8 flex items-center gap-0-5 md:gap-1-0 cursor-pointer"
        onClick={handleClick}
      >
        <img src={cartIconDark} alt="Cart" data-testId={`cart-${article.id}`} />
        <img src={plusIcon} alt="Plus" data-testId={`plus-${article.id}`} />
      </div>
    </>
  );
};

export default AddToCartButton;
